import { useState, useMemo, useEffect, useCallback } from "react";
import { useSession } from "next-auth/client";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import className from "classnames/bind";
import Image from "next/dist/client/image";
import IconBtnMenu from "../../public/svg/btnMenu.svg";
import IconLogoHead from "../../public/svg/logoHead.svg";
import styles from "./Header.module.scss";
import ContactUsButton from "../ContactUsButton";
import LoginButton from "../LoginButton";
import useWindowSize from "../../utils/hooks/findWindowSize";
import setHeightOfHeader from "../../utils/findHeightOfHeader";
import Unbroken from "../../public/img/unbroken.png";
import MilitarySport from "../../public/img/lviv-military.png";

const cx = className.bind(styles);

export default function Header() {
  const [menuActive, setMenuActive] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  const [session] = useSession();

  useEffect(() => {
    if (session) {
      setIsAdminLoggedIn(true);
    }
  }, [isAdminLoggedIn, session]);

  const router = useRouter();
  const size = useWindowSize();
  const heightOfHeader = useMemo(() => setHeightOfHeader(size.width), [size]);

  const handleMenuActive = () => {
    setMenuActive(!menuActive);
  };

  const handleMenuActiveFalse = (e) => {
    if (router.route === "/") {
      e.preventDefault();

      setMenuActive(false);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    setMenuActive(false);
  };

  const handleScrollToAnchor = (e) => {
    e.preventDefault();

    const anchor = document.getElementById(e.target.dataset.anchor);
    if (anchor) {
      const y =
        anchor.getBoundingClientRect().top +
        window.pageYOffset +
        heightOfHeader;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleSetRoute = (e) => {
    e.preventDefault();

    router.push({
      pathname: "/",
      query: { anchorName: e.target.dataset.anchor },
    });
  };

  const scrollToAnchor = useCallback(
    (id) => {
      const anchorTarget = document.getElementById(id);
      if (anchorTarget) {
        const y =
          anchorTarget.getBoundingClientRect().top +
          window.pageYOffset +
          heightOfHeader;

        window.scrollTo({ top: y, behavior: "smooth" });
      }
    },
    [heightOfHeader]
  );

  useEffect(() => {
    if (router.query.anchorName) {
      setTimeout(() => {
        scrollToAnchor(router.query.anchorName);
      }, 0);
    }
  }, [router, router.query.anchorName, scrollToAnchor]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
      </Head>

      <header className={styles.header}>
        <nav className={styles.nav}>
          <button className={styles.btnMenu} onClick={handleMenuActive}>
            <IconBtnMenu />
          </button>
          <div className={styles.logWrapper}>
            <div className={styles.unbroken}>
              <Link href="https://unbroken.org.ua/">
                <a target="_blank" rel="noopener noreferrer">
                  <Image
                    src={Unbroken}
                    alt="Unbroken"
                    width={100}
                    height={40}
                  />
                </a>
              </Link>
            </div>

            <Link href="/">
              <a
                role="link"
                tabIndex={0}
                className={styles.navLogo}
                onClick={handleMenuActiveFalse}
              >
                <IconLogoHead />
              </a>
            </Link>
          </div>

          <div
            role="button"
            tabIndex={0}
            className={cx("backdrop", {
              isOpen: menuActive,
            })}
            onClick={() => setMenuActive(false)}
          >
            <div className={styles.box}>
              <ul className={styles.navList}>
                <li
                  className={cx("navItem", {
                    active: router.pathname === "/playgrounds",
                  })}
                >
                  <Link href="/playgrounds">
                    <a className={styles.navLink}>Інфраструктура</a>
                  </Link>
                </li>
                <li
                  className={cx("navItem", {
                    active: router.pathname === "/map",
                  })}
                >
                  <Link href="/map">
                    <a className={styles.navLink}>карта</a>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link href="/?anchorName=navigateToAboutUs">
                    <a
                      role="link"
                      tabIndex={0}
                      className={styles.navLink}
                      data-anchor="navigateToAboutUs"
                      onClick={
                        router.route === "/"
                          ? handleScrollToAnchor
                          : handleSetRoute
                      }
                    >
                      про нас
                    </a>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link href="/?anchorName=navigateToNews">
                    <a
                      role="link"
                      tabIndex={0}
                      className={styles.navLink}
                      data-anchor="navigateToNews"
                      onClick={
                        router.route === "/"
                          ? handleScrollToAnchor
                          : handleSetRoute
                      }
                    >
                      новини
                    </a>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link href="/?anchorName=navigateToContacts">
                    <a
                      role="link"
                      tabIndex={0}
                      className={styles.navLink}
                      data-anchor="navigateToContacts"
                      onClick={
                        router.route === "/"
                          ? handleScrollToAnchor
                          : handleSetRoute
                      }
                    >
                      контакти
                    </a>
                  </Link>
                </li>
                <li
                  className={cx("navItem", {
                    active: router.pathname === "/unbroken",
                  })}
                >
                  <Link href="/unbroken">
                    <a className={styles.navLinkLatin}>unbroken</a>
                  </Link>
                </li>
                <div className={styles.unbroken}>
                  <Link href="https://lvivmilitarysports.svit.site/">
                    <a target="_blank" rel="noopener noreferrer">
                      <Image
                        src={MilitarySport}
                        alt="LvivMilitarySport"
                        width={100}
                        height={40}
                      />
                    </a>
                  </Link>
                </div>
              </ul>
              {isAdminLoggedIn ? (
                <LoginButton setIsAdminLoggedIn={setIsAdminLoggedIn} />
              ) : (
                <ContactUsButton shouldLockScreen={true} />
              )}
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
